
// src/components/UpgradePro.tsx
import React from 'react';
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import type { OnApproveData, OnApproveActions } from "@paypal/paypal-js";
import { SUBSCRIPTION_PLANS } from '../constants';
import type { ProPlan } from '../types';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';


interface UpgradeProProps {
 onUpgradeSuccess: (plan: ProPlan) => void;
 title?: string;
 description?: string;
}


export const UpgradePro: React.FC<UpgradeProProps> = ({ onUpgradeSuccess, title, description }) => {
 const [{ isPending, isRejected }] = usePayPalScriptReducer();
 const { showToast } = useToast();
 const { user } = useAuth();


 const monthlyPlanId = SUBSCRIPTION_PLANS.Monthly.id;
 const yearlyPlanId = SUBSCRIPTION_PLANS.Yearly.id;

 if (!monthlyPlanId || !yearlyPlanId) {
    return (
      <div className="upgrade-container p-8 text-center bg-yellow-50 border-l-4 border-yellow-500 rounded-md">
        <h3 className="text-lg font-bold text-yellow-800 mb-2">Payments Unavailable</h3>
        <p className="text-yellow-700">The payment system is currently not configured. Please check back later.</p>
      </div>
    );
 }

 const createSubscription = (data: Record<string, unknown>, actions: any, planId: string) => {
   return actions.subscription.create({ 
     plan_id: planId,
     // CRITICAL: Pass the Firebase User ID as custom_id so the webhook can link the payment to the user.
     custom_id: user?.uid 
   });
 };


 const onApprove = (data: OnApproveData, actions: OnApproveActions, plan: ProPlan) => {
   console.log("Subscription approved:", data);
   showToast(`Subscription successful! Your subscription ID is ${data.subscriptionID}`);
   // Call parent to save PRO status to the database (Optimistic update)
   onUpgradeSuccess(plan);
   return Promise.resolve();
 };


 const onError = (err: any) => {
   console.error("PayPal Subscription Error:", err);
   showToast("An error occurred with your subscription. Please try again.");
 };


 const onCancel = (data: Record<string, unknown>) => {
   console.log("Subscription cancelled:", data);
   showToast("You cancelled the subscription process.");
 };


 return (
   <div className="upgrade-container p-8 text-center bg-indigo-50 border-l-4 border-indigo-500 rounded-md">
     <h3 className="text-lg font-bold text-indigo-700 mb-2">{title || 'Upgrade to BrandGenius Pro'}</h3>
     <p className="text-indigo-600 mb-4">{description || 'Unlock unlimited generations with monthly or yearly plans.'}</p>


     {isPending && <div className="mb-4 animate-pulse">Loading PayPal...</div>}
     {isRejected && <div className="mb-4 text-red-600 font-semibold">Failed to load PayPal checkout. Please check your internet connection and try again.</div>}

     {!isPending && !isRejected && (
        <div className="plans-wrapper flex flex-col md:flex-row justify-center gap-8">
          {/* Monthly Plan */}
          <div className="plan-card border p-6 rounded-lg bg-white shadow-sm">
            <h4 className="text-xl font-semibold mb-2">Monthly Plan</h4>
            <p className="mb-4">$14.99 / month</p>
            <PayPalButtons
              key="monthly"
              style={{ layout: "vertical", label: "subscribe" }}
              createSubscription={(data, actions) => createSubscription(data, actions, monthlyPlanId)}
              onApprove={(data, actions) => onApprove(data, actions, 'Monthly')}
              onError={onError}
              onCancel={onCancel}
            />
          </div>


          {/* Yearly Plan */}
          <div className="plan-card border p-6 rounded-lg bg-white shadow-sm">
            <h4 className="text-xl font-semibold mb-2">Yearly Plan</h4>
            <p className="mb-4">$99.99 / year (Save ~44%)</p>
            <PayPalButtons
              key="yearly"
              style={{ layout: "vertical", label: "subscribe" }}
              createSubscription={(data, actions) => createSubscription(data, actions, yearlyPlanId)}
              onApprove={(data, actions) => onApprove(data, actions, 'Yearly')}
              onError={onError}
              onCancel={onCancel}
            />
          </div>
        </div>
      )}
   </div>
 );
};